import { SearchResponse } from "../models/SearchResponse";
import "../scss/_search-results.scss";
import { Box, Link, Pagination, styled, Typography } from "@mui/material";
import { SearchRecordDto } from "../models/SearchRecordDto";
import useKlasisRouting from "../utils/klasisRouting";
import { Link as RouterLink } from "react-router-dom";
import StatusBadge from "./StatusBadge";
import SearchHiglight from "../utils/searchHighlight";
import EmptySearchResults from "./EmptySearchResults";
import { useTranslation } from "react-i18next";
import HIGHLIGHT_ITEMS from "../constants/highlightItems";
import { Translations } from "../localization/translations";
import { getSuggestionDataState } from "../utils/klasisTracker";
import { SuggestionObjectType } from "../models/SuggestionObjectType";

const SearchItem = styled(Box)(({ theme }) => ({
    borderColor: theme.palette.divider,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    padding: "1rem .5rem 1rem .5rem",
}));

interface ISearchResultsProps {
    searchPhrase: string | null | undefined;
    data?: SearchResponse;
    onPageChange?: (page: number) => void;
}

const SearchResults: React.FC<ISearchResultsProps> = (
    props: ISearchResultsProps
) => {
    const { t } = useTranslation();

    if (!props.data || !props.data.totalRecords) {
        return (
            <EmptySearchResults
                title={t(Translations.common.label.noClassifications)}
                description={t(
                    Translations.common.label.noClassificationsDescription
                )}
            />
        );
    }

    const handlePageChange = (
        event: React.ChangeEvent<unknown>,
        page: number
    ): void => {
        if (!props.onPageChange) return;

        props.onPageChange(page);
    };

    return (
        <Box className="search-results">
            {props.data?.items
                .filter(
                    (value, index, self) =>
                        self.findIndex(
                            (v) => v.klasisData.id === value.klasisData.id
                        ) === index
                )
                .map((item) => (
                    <SearchResultItem
                        key={item.requestSuggestionId}
                        data={item}
                        requestId={props.data?.requestId}
                        searchPhrase={props.searchPhrase}
                    />
                ))}
            {
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "2rem",
                    }}
                >
                    <Pagination
                        count={props.data?.totalPages}
                        hideNextButton={!props.data?.hasNextPage}
                        hidePrevButton={!props.data?.hasPreviousPage}
                        page={props.data?.pageNumber}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Box>
            }
        </Box>
    );
};

interface ISearchResultItemProps {
    data?: SearchRecordDto;
    requestId?: string;
    searchPhrase: string | null | undefined;
}

const SearchResultItem: React.FC<ISearchResultItemProps> = (
    props: ISearchResultItemProps
) => {
    const { klasisRoute } = useKlasisRouting();

    const isTitleField = (fieldName: string) =>
        fieldName === HIGHLIGHT_ITEMS.title;

    const renderTitle = (): JSX.Element => {
        const highlightInTitle = props.data?.highlights.find((highlight) =>
            isTitleField(highlight.fieldName)
        );

        if (!highlightInTitle || !highlightInTitle.fragments.length)
            return (
                <Typography
                    variant="h6"
                    className="search-item-title-text"
                    sx={{ overflowWrap: "break-word" }}
                >
                    {props.data?.klasisData.title} {props.data?.klasisData.code ? `(${props.data?.klasisData.code})` : ""}
                </Typography>
            );

        const firstTitleFragment = highlightInTitle.fragments[0];

        return (
            <Typography
                variant="h6"
                className="search-item-title-text"
                sx={{ overflowWrap: "break-word" }}
            >
                {SearchHiglight.parse(firstTitleFragment)}
            </Typography>
        );
    };

    const renderBodyFragments = (): JSX.Element => {
        const highlightInDescription = props.data?.highlights.find(
            (highlight) => !isTitleField(highlight.fieldName)
        );

        if (
            !highlightInDescription ||
            !highlightInDescription.fragments.length
        ) {
            return (
                <Box>
                    <Typography variant="caption">
                        {props.data?.klasisData.description.substring(0, 150)}
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                {highlightInDescription.fragments.map((fragment, index) => {
                    return (
                        <Box key={`fragment-${index}`}>
                            <Typography variant="caption">
                                {SearchHiglight.parse(fragment)}
                            </Typography>
                        </Box>
                    );
                })}
            </>
        );
    };

    const getClassifierId = (): string =>
        props.data?.klasisData.klasisCode ??
        props.data?.klasisData.id.toString() ??
        "";

    const getTargetItemUrl = (): string => {
        if (props.data?.objectType === SuggestionObjectType.staticPage)
            return `static-page/${props.data?.klasisData?.code}`;

        return `classifications/${getClassifierId()}`;
    };

    if (!props.data) {
        return <></>;
    }

    return (
        <SearchItem className="search-item">
            <Box className="search-item-title">
                <Link
                    component={RouterLink}
                    to={klasisRoute(getTargetItemUrl())}
                    state={
                        props.searchPhrase
                            ? getSuggestionDataState(
                                  props.requestId,
                                  props.data?.requestSuggestionId
                              )
                            : {}
                    }
                >
                    {renderTitle()}
                </Link>
            </Box>
            <Box className="search-item-body-fragments">
                {renderBodyFragments()}
            </Box>
            {props.data.klasisData.region ? (
                <StatusBadge label={props.data.klasisData.region} />
            ) : null}
        </SearchItem>
    );
};

export default SearchResults;
